'use client';

import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import { Button } from '@/components/ui/button';

import { Checkbox } from '@/components/ui/checkbox';

import { Label } from '@/components/ui/label';

import { useSetupProfile } from '@/hooks/useSetupProfile';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { CategoryRequest, DropdownType } from '@/types/api/setup-profile';

interface FilterProps {
  selectedFilters: string[];
  filterType: CategoryRequest['type'];
  setSelectedFilters: Dispatch<SetStateAction<string[]>>;
  /**
   * IDEA_STARTER only: when set, sidebar shows only these parent groups (keys in /categories data),
   * in this order. Omit or leave empty to show all parents.
   */
  visibleParentCategoryNames?: string[];
  title?: string;
}

export const Filters = ({
  setSelectedFilters,
  selectedFilters,
  filterType = 'IDEA_STARTER',
  visibleParentCategoryNames,
  title = 'Categories',
}: FilterProps) => {
  const { getCategoriesApi } = useSetupProfile({
    loadCategories: true,
    filterType: filterType,
  });

  const [openCategories, setOpenCategories] = useState<string[]>([]);

  const isLoading = getCategoriesApi.isPending;

  const accordionEntries = useMemo(() => {
    const data = getCategoriesApi.data?.data;
    if (!data) return [] as [string, DropdownType[]][];

    let entries = Object.entries(data);

    if (filterType === 'IDEA_STARTER' && visibleParentCategoryNames?.length) {
      const allow = new Set(visibleParentCategoryNames);
      entries = entries.filter(([key]) => allow.has(key));
    }

    entries.sort(([a], [b]) => a.localeCompare(b));

    return entries;
  }, [getCategoriesApi.data, filterType, visibleParentCategoryNames]);

  // Set open categories when data is loaded - start with all collapsed
  useEffect(() => {
    if (getCategoriesApi.data?.data) {
      setOpenCategories([]);
    }
  }, [getCategoriesApi.data]);

  const handleFilterChange = (filterId: string, checked: boolean) => {
    setSelectedFilters((prev) => {
      if (checked) {
        return prev.includes(filterId) ? prev : [...prev, filterId];
      }
      return prev.filter((id) => id !== filterId);
    });
  };

  const isSubChecked = (item: DropdownType) =>
    selectedFilters.includes(item.id);

  const handleClearAll = () => {
    setSelectedFilters([]);
  };

  return (
    <aside className="sticky top-20 self-start py-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-normal">{title}</h2>

        <Button
          variant="ghost"
          className="text-xs hover:bg-ihs-blue/20"
          size="sm"
          onClick={handleClearAll}
        >
          Clear All
        </Button>
      </div>
      <div
        className={cn(
          'pr-4 ',
          filterType !== 'DISCUSSIONS' && 'max-h-[80vh] overflow-y-auto '
        )}
      >
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-6 w-full mb-2" />
            ))}
          </div>
        ) : filterType === 'TEASER' ? (
          <div className="flex flex-col gap-4 py-2">
            {Object.values(getCategoriesApi.data?.data || {})
              .flat()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <Checkbox
                    id={item.id}
                    checked={selectedFilters.includes(item.id)}
                    onCheckedChange={(checked) =>
                      handleFilterChange(item.id, checked === true)
                    }
                  />
                  <Label htmlFor={item.id}>{item.name}</Label>
                </div>
              ))}
          </div>
        ) : (
          <Accordion
            type="multiple"
            className="w-full"
            value={openCategories}
            onValueChange={setOpenCategories}
          >
            {accordionEntries.map(([key, value]) => (
              <AccordionItem className="border-none" value={key} key={key}>
                <AccordionTrigger
                  isChevron
                  className="text-sm justify-between w-full hover:bg-gray-50 rounded-md px-2 py-1 transition-colors"
                >
                  {key}
                </AccordionTrigger>

                <AccordionContent>
                  <div
                    className={cn(
                      'max-h-[200px] overflow-y-auto w-full',
                      filterType === 'DISCUSSIONS' && 'max-h-[75vh]'
                    )}
                  >
                    <div className="flex flex-col gap-4 py-2 px-4">
                      {value
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-2"
                          >
                            <Checkbox
                              id={item.id}
                              checked={isSubChecked(item)}
                              onCheckedChange={(checked) =>
                                handleFilterChange(item.id, checked === true)
                              }
                            />

                            <Label htmlFor={item.id}>{item.name}</Label>
                          </div>
                        ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </aside>
  );
};
